import type { WizardState, Company, Renter } from './types'
import { SectionBlock } from './ui'
import type { SigningOrderResult, SigningOrderStatus } from '../../../lib/signing'

const BLUE = '#4f46e5'

// ─── Icons ───────────────────────────────────────────────────────────────────
const IconClipboard = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
    <rect x="8" y="2" width="8" height="4" rx="1"/>
    <path d="M9 12h6M9 16h4"/>
  </svg>
)
const IconCoin = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 6v2m0 8v2M9.5 9.5A2.5 2.5 0 0 1 12 8h.5a2 2 0 0 1 0 4h-1a2 2 0 0 0 0 4h.5a2.5 2.5 0 0 0 2.5-2.5"/>
  </svg>
)
const IconSendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/>
    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
)
const IconDoc = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <path d="M14 2v6h6M16 13H8M16 17H8"/>
  </svg>
)
const IconEye = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
)

// ─── Helpers ──────────────────────────────────────────────────────────────────
function parseDkk(s: string): number {
  return parseFloat((s || '').replace(/\./g, '').replace(',', '.')) || 0
}
function fmtDkk2(n: number): string {
  return n.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr.'
}
function fmtDate(iso: string): string {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

// ─── Summary table row ────────────────────────────────────────────────────────
function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  if (!value) return null
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
      <span style={{ fontSize: 13, color: '#6b7280' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: bold ? 700 : 500, color: bold ? '#111827' : '#374151' }}>{value}</span>
    </div>
  )
}

// ─── Bilag badge ─────────────────────────────────────────────────────────────
function BilagBadge({ label }: { label: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '9px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fafafa',
      fontSize: 13, color: '#374151', marginBottom: 6,
    }}>
      <span style={{ color: '#6b7280', display: 'flex' }}><IconDoc /></span>
      <span>{label}</span>
    </div>
  )
}

interface Props {
  state: WizardState
  onChange: (p: Partial<WizardState>) => void
  company: Company | null
  renters: Renter[]
  onSend: () => void
  onPreview: () => void
  saving?: boolean
  // Idura MitID signing (step F) — sent-state surfaced after the order is created.
  signingResult?: SigningOrderResult | null
  signingStatus?: SigningOrderStatus | null
  sendError?: string | null
  onSigningDone?: () => void
}

export function SectionSend({ state, company, renters, onSend, onPreview, saving, signingResult, signingStatus, sendError, onSigningDone }: Props) {
  const renter = renters.find(r => r.id === state.renter_id)

  // Party display name for the sent-state list: landlord = company, tenant = renter.
  const partyLabel = (role: string, renterId: string | null): string => {
    if (role === 'landlord') return company?.name ? `${company.name} (udlejer)` : 'Udlejer'
    const r = renters.find(x => x.id === renterId)
    return r?.name ?? (role === 'co_tenant' ? 'Medlejer' : 'Lejer')
  }
  const isSigned = (signatoryId: string): boolean =>
    !!signingStatus?.signatories.find(s => s.signatory_id === signatoryId)?.signed_at

  const monthlyRent = parseDkk(state.monthly_rent_dkk)
  const deposit = parseDkk(state.deposit_dkk)
  const prepaid = parseDkk(state.prepaid_rent_dkk)

  const acontoTotal = [
    state.aconto_heating_dkk, state.aconto_water_dkk, state.aconto_electricity_dkk,
    state.aconto_cooling_dkk, state.aconto_antenna_dkk, state.aconto_internet_dkk,
    state.beboer_amount_dkk,
    ...(state.extra_contributions ?? []).map(c => c.dkk),
  ].reduce((s, v) => s + parseDkk(v), 0)

  const engangsTotal = (state.engangsudgifter ?? []).reduce((s, e) => s + parseDkk(e.dkk), 0)
  const monthlyTotal = monthlyRent + acontoTotal
  const indflytningTotal = deposit + prepaid + engangsTotal

  const bilag: string[] = []
  if (state.husorden === 'standard') bilag.push('Standard husorden fra Boligflow')
  if (state.husorden === 'ejendom') bilag.push('Husorden fra ejendom')
  if (state.husorden === 'ja') bilag.push('Egen husorden (PDF)')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── Lejeforhold ───────────────────────────────────────────────────── */}
      <SectionBlock icon={<IconClipboard />} title="Lejeforholdet" subtitle="Tjek at oplysningerne er korrekte">
        <Row label="Adresse" value={[state.property_address, state.property_city].filter(Boolean).join(', ')} />
        <Row label="Areal" value={state.area_m2 ? `${state.area_m2} m2` : ''} />
        <Row label="Indflytningsdato" value={fmtDate(state.start_date)} />
        {state.end_date && <Row label="Fraflytningsdato" value={fmtDate(state.end_date)} />}
        <Row label="Lejer" value={renter?.name ?? (state.indhent_email || '')} />
        <Row label="Lejer e-mail" value={renter?.email ?? state.indhent_email ?? ''} />
      </SectionBlock>

      {/* ── Husleje og forbrug ────────────────────────────────────────────── */}
      <SectionBlock icon={<IconCoin />} title="Husleje og forbrug" subtitle="Løbende månedlige betalinger">
        <Row label="Månedlig husleje" value={monthlyRent ? fmtDkk2(monthlyRent) : ''} />
        {acontoTotal > 0 && <Row label="A conto og bidrag" value={fmtDkk2(acontoTotal)} />}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 0', borderTop: '2px solid #f3f4f6', marginTop: 4 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Total pr. måned</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: monthlyTotal > 0 ? '#111827' : '#9ca3af' }}>
            {monthlyTotal > 0 ? fmtDkk2(monthlyTotal) : '0,00 kr.'}
          </span>
        </div>
      </SectionBlock>

      {/* ── Indbetalinger ved indflytning ─────────────────────────────────── */}
      <SectionBlock icon={<IconCoin />} title="Indbetalinger ved indflytning" subtitle="Engangsposter der betales ved indflytning">
        {deposit > 0 && <Row label="Depositum" value={fmtDkk2(deposit)} />}
        {prepaid > 0 && <Row label="Forudbetalt leje" value={fmtDkk2(prepaid)} />}
        {(state.engangsudgifter ?? []).map((e, i) => (
          e.dkk && parseDkk(e.dkk) > 0 ? (
            <Row key={i} label={e.name || `Engangsudfigt ${i + 1}`} value={fmtDkk2(parseDkk(e.dkk))} />
          ) : null
        ))}
        {deposit === 0 && prepaid === 0 && engangsTotal === 0 ? (
          <div style={{ fontSize: 13, color: '#9ca3af', padding: '8px 0' }}>Ingen engangsposter registreret</div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 0', borderTop: '2px solid #f3f4f6', marginTop: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Total for indflytning</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{fmtDkk2(indflytningTotal)}</span>
          </div>
        )}
      </SectionBlock>

      {/* ── Bilag ─────────────────────────────────────────────────────────── */}
      <SectionBlock icon={<IconDoc />} title="Bilag" subtitle="Dokumenter der vedlægges kontrakten">
        {bilag.length === 0 ? (
          <div style={{ fontSize: 13, color: '#9ca3af' }}>Ingen bilag tilføjet</div>
        ) : (
          bilag.map((b, i) => <BilagBadge key={i} label={b} />)
        )}
      </SectionBlock>

      {/* ── Afsend / sendt-tilstand ───────────────────────────────────────── */}
      <SectionBlock icon={<IconSendIcon />} title="Underskrift med MitID" subtitle="Kontrakten sendes til digital signering med MitID">
        {signingResult ? (
          <div>
            <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#065f46' }}>Sendt til underskrift</div>
              <div style={{ fontSize: 12, color: '#047857', lineHeight: 1.6, marginTop: 2 }}>
                {signingResult.is_stub
                  ? 'Underskriftsordren er oprettet (teststub — ingen rigtig MitID-signering). Hver part har fået et signeringslink.'
                  : 'Hver part har modtaget en e-mail med sit personlige MitID-signeringslink. Status opdateres automatisk, efterhånden som parterne underskriver.'}
              </div>
            </div>
            {signingResult.signatories.map(p => {
              const signed = isSigned(p.signatory_id)
              return (
                <div key={p.signatory_id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div>
                    <div style={{ fontSize: 14, color: '#111827' }}>{partyLabel(p.role, p.renter_id)}</div>
                    <a href={p.href} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: BLUE, textDecoration: 'none', wordBreak: 'break-all' }}>Åbn signeringslink</a>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(p.href)}
                      style={{ marginLeft: 10, fontSize: 11, color: '#6b7280', background: 'none', border: '1px solid #e5e7eb', borderRadius: 5, padding: '2px 7px', cursor: 'pointer' }}
                    >
                      Kopiér link
                    </button>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: signed ? '#065f46' : '#92400e', background: signed ? '#ecfdf5' : '#fffbeb', border: `1px solid ${signed ? '#a7f3d0' : '#fde68a'}`, borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap' }}>
                    {signed ? 'Underskrevet' : 'Afventer'}
                  </span>
                </div>
              )
            })}
            {onSigningDone && (
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button onClick={onSigningDone} style={{ padding: '10px 22px', border: 'none', borderRadius: 7, background: BLUE, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  Færdig
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {!renter && !state.indhent_email ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fca5a5', fontSize: 13, color: '#dc2626', marginBottom: 12 }}>
                Vælg en lejer under trin 8, før du kan sende.
              </div>
            ) : (
              <div style={{ fontSize: 13, color: '#374151', marginBottom: 16, lineHeight: 1.6 }}>
                Kontrakten sendes til <strong>{renter?.name ?? state.indhent_email}</strong>
                {renter?.email && ` (${renter.email})`} til digital underskrift med MitID. Udlejer og lejer modtager hver et personligt signeringslink.
              </div>
            )}
            {sendError && (
              <div style={{ fontSize: 12, color: '#dc2626', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>{sendError}</div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={onPreview}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  padding: '10px', border: '1px solid #d1d5db', borderRadius: 7,
                  background: 'white', fontSize: 13, cursor: 'pointer', color: '#374151', fontWeight: 500,
                }}
              >
                <IconEye /> Se forhåndsvisning
              </button>
              <button
                onClick={onSend}
                disabled={(!renter && !state.indhent_email) || saving}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  padding: '10px', border: 'none', borderRadius: 7,
                  background: ((renter || state.indhent_email) && !saving) ? BLUE : '#d1d5db',
                  color: 'white', fontSize: 13, fontWeight: 600,
                  cursor: ((renter || state.indhent_email) && !saving) ? 'pointer' : 'default',
                }}
              >
                <IconSendIcon /> {saving ? 'Sender…' : 'Send til underskrift med MitID'}
              </button>
            </div>
          </>
        )}
      </SectionBlock>

    </div>
  )
}
